import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import MainLayout from '@/components/layouts/MainLayout';
import { authStore } from '@/stores/authStore';
import { Loader2 } from 'lucide-react';
import { Applications } from '@/components/applications/Applications';
import type { Application, OrganizationApplication } from '@/components/applications/Applications';
import { axiosInstance } from '@/utils/axiosConfig';
import { OrganizationMembers } from '@/components/organizations/OrganizationMembers';
import { showToast } from '@/utils/toast';
import { useTranslation } from 'react-i18next';

const DashboardPage = observer(() => {
  const user = authStore.user;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [orgApplications, setOrgApplications] = useState<OrganizationApplication[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        setError(null);
        const orgId = authStore.currentOrganization?.id;
        
        // Always fetch available applications
        const appsResponse = await axiosInstance.get('/applications');
        setApplications(appsResponse.data);
        
        // Fetch org applications only if we have an organization
        if (orgId) {
          const orgAppsResponse = await axiosInstance.get(`/applications/organizations/${orgId}`);
          setOrgApplications(orgAppsResponse.data);
          
          // Show success toast after successful fetch
          showToast.success({
            description: 'Applications loaded successfully'
          });
        }
      } catch (error: any) {
        console.error('Error fetching applications:', error);
        setError('Failed to load applications. Please try again later.');
        showToast.error({
          description: error.response?.data?.message || 'Failed to load applications'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [authStore.currentOrganization?.id]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('navigation.dashboard')}, {user?.name}!
          </h1>
          <p className="text-gray-600">
            {t('navigation.organization')}: {authStore.currentOrganization?.name}
          </p>
        </div>

        <Applications
          applications={applications}
          orgApplications={orgApplications}
          loading={loading}
          error={error}
          setError={setError}
          setOrgApplications={setOrgApplications}
          setLoading={setLoading}
        />

        <OrganizationMembers
          loading={loading}
          error={error}
          setError={setError}
        />
      </div>
    </MainLayout>
  );
});

export default DashboardPage; 